import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from 'api/api-context';
import BookingLayout from 'pages/booking/BookingLayout';
import { BOOKING_SLUG_THANKYOU_PATH } from 'routes/paths';
import useLang from 'lang';

/** Reconciles a Stripe Checkout return before showing the local confirmation state. */
export default function BookingPaymentReturn() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAction, jwt } = useApi();
  const { _t } = useLang();
  const [error, setError] = useState(false);

  useEffect(() => {
    /** Runs one authoritative provider refresh and removes sensitive query values. */
    const reconcile = async () => {
      const confirmation = JSON.parse(sessionStorage.getItem('bookingConfirmation') ?? 'null') ?? {};
      const queryToken = searchParams.get('payment_access_token');
      if (queryToken) sessionStorage.setItem('bookingPaymentAccessToken', queryToken);
      sessionStorage.setItem('bookingStripeReturned', 'yes');
      window.history.replaceState({}, document.title, window.location.pathname);
      try {
        const response: any = jwt
          ? await getAction('RefreshReservationPayment')({
              parameters: { reservationId: confirmation.reservation?.id },
            })
          : await getAction('RefreshPublicReservationPayment')({
              parameters: { accessToken: queryToken || sessionStorage.getItem('bookingPaymentAccessToken') || '' },
            });
        const next = {
          ...confirmation,
          payment: response.payment,
          reservation: {
            ...confirmation.reservation,
            ...(response.reservation || {}),
            temporaryPinCode: response.reservation?.temporaryPinCode || response.temporaryPin?.pinCode || null,
            pinValidFrom: response.reservation?.startsAt,
            pinValidTo: response.reservation?.endsAt,
          },
        };
        sessionStorage.setItem('bookingConfirmation', JSON.stringify(next));
        navigate(BOOKING_SLUG_THANKYOU_PATH.insert({ slug }), { replace: true, state: next });
      } catch {
        setError(true);
      }
    };
    void reconcile();
  }, [getAction, jwt, navigate, searchParams, slug]);

  return (
    <BookingLayout>
      <div className="thankyou-page">
        <div className="thankyou-inner">
          <h1 className="thankyou-title">{error ? _t('Payment status unavailable') : _t('Verifying payment...')}</h1>
          {error && (
            <p>{_t('We could not verify the payment yet. Please return to the confirmation page and retry.')}</p>
          )}
        </div>
      </div>
    </BookingLayout>
  );
}
